/**
 * Le lien d'un coupon — [DECISIONS §330].
 *
 * **Une fonction pure, et pour la même raison que `vitrine/routes.ts`.**
 * `App.tsx` est le fichier le plus disputé du dépôt : le lien de coupon y tient
 * en une ligne, et tout ce qui sait « à quoi ressemble cette adresse » vit ici,
 * où il se teste sans monter un seul composant.
 *
 * **Le chemin est court exprès.** `/coupon/4KJ9P2WDMX` se recopie à la main
 * depuis une image imprimée, et se dicte au téléphone. Un paramètre de requête
 * — `?code=` — se serait fait couper par les messageries, qui détectent la fin
 * d'un lien à l'espace mais pas toujours à l'esperluette.
 */

/**
 * **L'adresse est écrite en dur, et c'est le seul endroit où elle l'est.**
 *
 * `window.location.origin` aurait paru plus propre. Il produit
 * `http://localhost:8880/coupon/…` sur la machine de l'éditeur qui exporte —
 * c'est-à-dire un lien mort sur tous les téléphones à qui il l'envoie, et un
 * lot de cent coupons gâchés sans que rien ne le signale. Ce qui est exporté
 * quitte la machine : il ne peut pas dépendre d'elle.
 */
export const ADRESSE_PUBLIQUE = 'https://haicademy.com'

/** La racine du chemin, partagée par ce qui fabrique le lien et ce qui le lit. */
export const CHEMIN_DU_COUPON = '/coupon'

/**
 * Le code tel qu'il se tape — dix caractères d'un alphabet sans 0, O, 1, I ni L.
 *
 * **Le MÊME motif que le serveur**, et il doit le rester : le jour où l'un des
 * deux s'élargit sans l'autre, l'écran promet un code que le serveur refusera,
 * ou l'inverse. Les longueurs sont bornées largement — 6 à 32 — parce que ce
 * motif ne décide de rien : il écarte ce qui n'est manifestement pas un code
 * (un chemin, un fragment de phrase) avant de préremplir un champ avec.
 */
const CODE = /^[A-Z0-9]{6,32}$/

/** L'adresse à écrire sur une carte, à copier dans WhatsApp, à imprimer. */
export function lienDuCoupon(code: string): string {
  return `${ADRESSE_PUBLIQUE}${CHEMIN_DU_COUPON}/${code}`
}

/**
 * Le code porté par un chemin — `null` quand ce chemin n'est pas un lien de
 * coupon.
 *
 * **Tout ce qui suit `/coupon/` est normalisé avant d'être jugé.** Une
 * messagerie ajoute volontiers une barre finale au lien qu'elle détecte, et un
 * candidat qui recopie à la main écrit volontiers en minuscules ou sépare les
 * groupes de quatre par des espaces — ce que l'écran lui montre. Les trois se
 * ramènent au même code, parce que ce sont les trois façons dont ce lien arrive
 * réellement.
 */
export function codeDuLien(chemin: string): string | null {
  if (chemin !== CHEMIN_DU_COUPON && !chemin.startsWith(`${CHEMIN_DU_COUPON}/`)) return null

  const brut = decoupe(chemin.slice(CHEMIN_DU_COUPON.length))

  return CODE.test(brut) ? brut : null
}

/** `« 4kj9 p2wd mx »` et `« /4KJ9-P2WD-MX/ »` désignent le même coupon. */
function decoupe(reste: string): string {
  return reste.replace(/^\/+|\/+$/g, '').replace(/[\s-]/g, '').toUpperCase()
}

/**
 * L'export texte d'un lot : une ligne par coupon, et rien d'autre.
 *
 * **C'est le format le plus utile du lot, et c'est délibérément le plus
 * pauvre.** WhatsApp est le canal réel de ce produit : on y colle une liste, on
 * la découpe au doigt, on envoie une ligne à chacun. Une ligne qui porterait
 * aussi la formule et l'échéance obligerait à faire le tri avant d'envoyer, et
 * personne ne le ferait cent fois.
 */
export function lignesDeLiens(codes: readonly string[]): string {
  return codes.map(lienDuCoupon).join('\n')
}
